/**
 * Live H.264 capture for evaluation clips and match chunks.
 *
 * Frames are painted into an offscreen canvas and encoded while the camera
 * is open. The file that stops is already an H.264 MP4 at the requested
 * bitrate (Low = 900 kbps, about 8 MB/min). There is no playback remux.
 * The picture is silent. Chrome and Safari both record the mic as a second
 * file and the server copies this video and muxes that audio in.
 * Phones without WebCodecs get null from createLiveH264Session and keep
 * the MediaRecorder path.
 */

import { canUseWebCodecsReelEncoder, pickAvcConfig } from "./reelWebCodecsEncoder";
import { EVAL_VIDEO_AUDIO_BITRATE } from "./evalVideoEncoding";

export function evenEncodeSize(width, height) {
  const w = Math.max(2, Math.floor(Number(width) || 0));
  const h = Math.max(2, Math.floor(Number(height) || 0));
  return {
    width: w - (w % 2) || 2,
    height: h - (h % 2) || 2,
  };
}

export function liveFrameIntervalMs(fps) {
  const rate = Number(fps) > 0 ? Number(fps) : 30;
  return 1000 / rate;
}

/** True when enough wall time has passed to encode the next frame. */
export function liveFrameDue(now, last, fps) {
  if (!Number.isFinite(last)) return true;
  return now - last >= liveFrameIntervalMs(fps) * 0.85;
}

/** Microseconds from the start of this segment. */
export function liveFrameTimestampUs(segmentStartedAt, now) {
  const start = Number(segmentStartedAt) || 0;
  const t = Number(now) || 0;
  return Math.max(0, Math.round((t - start) * 1000));
}

export function stampLiveH264Blob(blob, encodeWallMs) {
  if (!blob) return blob;
  blob._smartttLiveH264 = true;
  blob._smartttExt = "mp4";
  if (encodeWallMs != null) blob._smartttEncodeWallMs = encodeWallMs;
  return blob;
}

export function isLiveH264Blob(blob) {
  return Boolean(blob && blob._smartttLiveH264);
}

export function liveH264EncodeWallMs(blob) {
  const n = Number(blob?._smartttEncodeWallMs);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function webAudio() {
  const g = typeof window !== "undefined" ? window : {};
  return {
    AudioEncoder: g.AudioEncoder,
    MediaStreamTrackProcessor: g.MediaStreamTrackProcessor,
  };
}

async function pickLiveAvcConfig(width, height, fps, bitrate) {
  const constant = await pickAvcConfig(width, height, fps, bitrate, "constant");
  if (constant) return constant;
  return pickAvcConfig(width, height, fps, bitrate);
}

async function pickAacConfig(sampleRate, channels, bitrate) {
  const { AudioEncoder } = webAudio();
  if (typeof AudioEncoder !== "function" || !AudioEncoder.isConfigSupported) {
    return null;
  }
  const rates = [bitrate, 128_000, 64_000].filter(
    (n, i, all) => Number(n) > 0 && all.indexOf(n) === i
  );
  for (const rate of rates) {
    const config = {
      codec: "mp4a.40.2",
      sampleRate,
      numberOfChannels: channels,
      bitrate: rate,
    };
    try {
      const result = await AudioEncoder.isConfigSupported(config);
      if (result?.supported) return result.config || config;
    } catch {
      /* try the next rate */
    }
  }
  return null;
}

function audioTrackFormat(track) {
  const settings = track?.getSettings?.() || {};
  const sampleRate = Number(settings.sampleRate) || 48000;
  const channels = Number(settings.channelCount) === 2 ? 2 : 1;
  return { sampleRate, channels };
}

/**
 * @param {{
 *   width: number,
 *   height: number,
 *   fps?: number,
 *   bitrate: number,
 *   audioTrack?: MediaStreamTrack|null,
 *   audioBitrate?: number,
 *   paint: (ctx: CanvasRenderingContext2D, width: number, height: number) => boolean,
 *   onEncodedBytes?: (bytes: number) => void,
 * }} options
 * @returns {Promise<LiveH264Session|null>}
 */
export async function createLiveH264Session(options) {
  if (!canUseWebCodecsReelEncoder()) return null;
  if (typeof options?.paint !== "function") return null;
  const session = new LiveH264Session(options);
  try {
    const opened = await session.open();
    if (!opened) {
      session.close();
      return null;
    }
    return session;
  } catch {
    try {
      session.close();
    } catch {
      /* ignore */
    }
    return null;
  }
}

class LiveH264Session {
  constructor(options) {
    const size = evenEncodeSize(options.width, options.height);
    this.width = size.width;
    this.height = size.height;
    this.fps = Number(options.fps) > 0 ? Number(options.fps) : 30;
    this.bitrate = Math.max(100_000, Number(options.bitrate) || 900_000);
    this.audioTrack = options.audioTrack || null;
    this.requireAudio = Boolean(options.requireAudio);
    this.audioBitrate = Number(options.audioBitrate) || EVAL_VIDEO_AUDIO_BITRATE;
    this.paint = options.paint;
    this.onEncodedBytes = options.onEncodedBytes;
    this.videoConfig = null;
    this.audioConfig = null;
    this.segment = null;
    this.closed = false;
    this.raf = 0;
    this.lastFrameAt = NaN;
    this.audioChain = Promise.resolve();
    this.audioReader = null;
    this.pendingAudioReader = null;
    this.encodedBytes = 0;
    this.op = Promise.resolve();
  }

  async open() {
    this.videoConfig = await pickLiveAvcConfig(
      this.width,
      this.height,
      this.fps,
      this.bitrate
    );
    if (!this.videoConfig) return false;
    if (this.audioTrack && this.audioTrack.readyState === "live") {
      const format = audioTrackFormat(this.audioTrack);
      this.audioConfig = await pickAacConfig(
        format.sampleRate,
        format.channels,
        this.audioBitrate
      );
    }
    if (this.audioConfig && !this.audioPumpAvailable()) {
      this.audioConfig = null;
    }
    if (this.requireAudio && !this.audioConfig) return false;
    this.segment = await this.openSegment();
    if (!this.segment) return false;
    this.startAudioPump();
    this.loop();
    return true;
  }

  audioPumpAvailable() {
    const { MediaStreamTrackProcessor } = webAudio();
    if (typeof MediaStreamTrackProcessor !== "function" || !this.audioTrack) {
      return false;
    }
    try {
      const processor = new MediaStreamTrackProcessor({ track: this.audioTrack });
      this.pendingAudioReader = processor.readable.getReader();
      return true;
    } catch {
      return false;
    }
  }

  async openSegment() {
    const { Muxer, ArrayBufferTarget } = await import("mp4-muxer");
    const target = new ArrayBufferTarget();
    const muxer = new Muxer({
      target,
      video: {
        codec: "avc",
        width: this.width,
        height: this.height,
        frameRate: this.fps,
      },
      audio: this.audioConfig
        ? {
            codec: "aac",
            numberOfChannels: this.audioConfig.numberOfChannels,
            sampleRate: this.audioConfig.sampleRate,
          }
        : undefined,
      fastStart: "in-memory",
      firstTimestampBehavior: "offset",
    });
    let encoderError = null;
    const videoEncoder = new window.VideoEncoder({
      output: (chunk, meta) => {
        try {
          muxer.addVideoChunk(chunk, meta);
          this.encodedBytes += chunk.byteLength || 0;
          this.onEncodedBytes?.(this.encodedBytes);
        } catch (err) {
          encoderError = err;
        }
      },
      error: (err) => {
        encoderError = err;
      },
    });
    videoEncoder.configure(this.videoConfig);

    let audioEncoder = null;
    if (this.audioConfig && typeof window.AudioEncoder === "function") {
      audioEncoder = new window.AudioEncoder({
        output: (chunk, meta) => {
          try {
            muxer.addAudioChunk(chunk, meta);
          } catch (err) {
            encoderError = err;
          }
        },
        error: (err) => {
          encoderError = err;
        },
      });
      try {
        audioEncoder.configure(this.audioConfig);
      } catch {
        try {
          audioEncoder.close();
        } catch {
          /* ignore */
        }
        audioEncoder = null;
        this.audioConfig = null;
      }
    }

    const canvas = document.createElement("canvas");
    canvas.width = this.width;
    canvas.height = this.height;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) {
      try {
        videoEncoder.close();
      } catch {
        /* ignore */
      }
      return null;
    }
    return {
      muxer,
      target,
      videoEncoder,
      audioEncoder,
      canvas,
      ctx,
      startedAt: performance.now(),
      lastUs: -1,
      lastKeyUs: -1_000_000,
      frames: 0,
      closing: false,
      takeError: () => encoderError,
    };
  }

  startAudioPump() {
    const reader = this.pendingAudioReader;
    this.pendingAudioReader = null;
    if (!this.audioConfig || !reader) {
      try {
        reader?.cancel();
      } catch {
        /* ignore */
      }
      return;
    }
    this.audioReader = reader;
    const pump = async () => {
      try {
        while (!this.closed) {
          const { done, value } = await reader.read();
          if (done || !value) break;
          const data = value;
          this.audioChain = this.audioChain.then(() => {
            const enc = this.segment?.audioEncoder;
            const expected = this.audioConfig?.sampleRate;
            if (
              !enc ||
              this.segment?.closing ||
              (expected && data.sampleRate && data.sampleRate !== expected)
            ) {
              data.close();
              return;
            }
            try {
              enc.encode(data);
            } catch {
              data.close();
              return;
            }
            data.close();
          });
        }
      } catch {
        /* track ended */
      }
    };
    void pump();
  }

  loop() {
    this.raf = window.requestAnimationFrame(() => {
      this.onFrame(performance.now());
      if (!this.closed) this.loop();
    });
  }

  onFrame(now) {
    const seg = this.segment;
    if (!seg || seg.closing || this.closed) return;
    const failed = seg.takeError();
    if (failed) return;
    if (!liveFrameDue(now, this.lastFrameAt, this.fps)) return;
    if (seg.videoEncoder.encodeQueueSize > 6) return;
    this.lastFrameAt = now;
    let painted = false;
    try {
      painted = seg.ctx && this.paint(seg.ctx, this.width, this.height) !== false;
    } catch {
      painted = false;
    }
    if (!painted) return;
    let timestamp = liveFrameTimestampUs(seg.startedAt, now);
    if (timestamp <= seg.lastUs) timestamp = seg.lastUs + 1;
    const keyFrame = timestamp - seg.lastKeyUs >= 1_000_000;
    let frame;
    try {
      frame = new window.VideoFrame(seg.canvas, {
        timestamp,
        duration: Math.max(1, timestamp - Math.max(0, seg.lastUs)),
      });
    } catch {
      return;
    }
    try {
      seg.videoEncoder.encode(frame, { keyFrame });
      seg.lastUs = timestamp;
      if (keyFrame) seg.lastKeyUs = timestamp;
      seg.frames += 1;
    } catch {
      /* encoder rejected this frame */
    } finally {
      frame.close();
    }
  }

  /**
   * Flush the open segment and start the next one.
   * @returns {Promise<Blob|null>}
   */
  finishSegment() {
    this.op = this.op.then(() => this.flushSegment(false));
    return this.op;
  }

  /** Flush the last segment and stop. */
  stop() {
    this.closed = true;
    if (this.raf) {
      window.cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
    this.op = this.op.then(() => this.flushSegment(true));
    return this.op;
  }

  close() {
    this.closed = true;
    this.discard = true;
    if (this.raf) {
      window.cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
    const seg = this.segment;
    this.segment = null;
    try {
      this.audioReader?.cancel();
    } catch {
      /* ignore */
    }
    try {
      this.pendingAudioReader?.cancel();
    } catch {
      /* ignore */
    }
    this.audioReader = null;
    this.pendingAudioReader = null;
    try {
      seg?.videoEncoder?.close();
    } catch {
      /* ignore */
    }
    try {
      seg?.audioEncoder?.close();
    } catch {
      /* ignore */
    }
  }

  async flushSegment(final) {
    if (this.discard) return null;
    const seg = this.segment;
    if (!seg || seg.closing) return null;
    seg.closing = true;
    this.segment = null;
    const started = Date.now();
    let reopened = false;
    if (!final && !this.closed) {
      // Open the next segment before flushing this one so frames keep flowing.
      // A flush-then-open gap drops ~100 ms per rotation and drifts the
      // continuous sidecar audio against the concatenated picture.
      try {
        const next = await this.openSegment();
        if (next && !this.closed && !this.discard) {
          this.segment = next;
          this.encodedBytes = 0;
          this.onEncodedBytes?.(0);
          reopened = true;
        } else if (next) {
          try {
            next.videoEncoder.close();
            next.audioEncoder?.close();
          } catch {
            /* ignore */
          }
        }
      } catch {
        reopened = false;
      }
    }
    await this.audioChain;
    try {
      if (seg.videoEncoder.state !== "closed") await seg.videoEncoder.flush();
      if (seg.audioEncoder && seg.audioEncoder.state !== "closed") {
        await seg.audioEncoder.flush();
      }
    } catch {
      /* flush what we can */
    }
    try {
      seg.videoEncoder.close();
    } catch {
      /* ignore */
    }
    try {
      seg.audioEncoder?.close();
    } catch {
      /* ignore */
    }
    let blob = null;
    if (seg.frames >= 1) {
      try {
        seg.muxer.finalize();
        blob = stampLiveH264Blob(
          new Blob([seg.target.buffer], { type: "video/mp4" }),
          Date.now() - started
        );
      } catch {
        blob = null;
      }
    }
    if (reopened) {
      /* next segment is already recording */
    } else if (!final && !this.closed && !this.discard) {
      this.encodedBytes = 0;
      this.onEncodedBytes?.(0);
      this.lastFrameAt = NaN;
      this.segment = await this.openSegment();
    } else {
      try {
        await this.audioReader?.cancel();
      } catch {
        /* ignore */
      }
      this.audioReader = null;
    }
    return blob;
  }
}
